import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { navigationLinks } from '../../config/content';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import Logo from '../common/Logo';

function Navigation({ isMenuOpen: propIsMenuOpen, setIsMenuOpen: propSetIsMenuOpen }) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [profile, setProfile] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [internalIsMenuOpen, setInternalIsMenuOpen] = useState(false);

  // Use props if provided, otherwise use internal state
  const isMenuOpen = propIsMenuOpen !== undefined ? propIsMenuOpen : internalIsMenuOpen;
  const setIsMenuOpen = propSetIsMenuOpen || setInternalIsMenuOpen;

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await signOut();
      if (!error) {
        navigate('/');
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getProfileMenuItems = () => {
    if (profile?.user_type === 'institution') {
      return [
        {
          label: 'Institute Info',
          path: '/institute-details',
        },
        {
          label: 'Browse Job Posts',
          path: '/',
        },
        {
          label: 'Institute Profile',
          path: '/',
        },
      ];
    }

    return [
      {
        label: 'Upload Profile',
        path: '/personal-information',
      },
      {
        label: 'Browse Jobs',
        path: '/',
      },
      {
        label: 'My Profile',
        path: '/dashboard',
      },
    ];
  };

  return (
    <nav className="bg-primary-extra shadow-sm fixed w-full z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Logo />
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-primary-text hover:text-primary-500 focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Desktop navigation */}
          <div className="hidden md:flex md:flex-1 justify-center">
            <div className="flex items-center space-x-8">
              {navigationLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  className="text-primary-text hover:text-primary-500 transition-colors duration-200 text-sm font-medium"
                >
                  {link.title}
                </a>
              ))}
            </div>
          </div>

          {/* Action buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center space-x-2 text-primary-text hover:text-primary-500 focus:outline-none"
                >
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden">
                    {profile?.profile_picture_url ? (
                      <img 
                        src={profile.profile_picture_url} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                  </div>
                </button>

                {/* Profile Dropdown */}
                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg py-1">
                    {/* User Info Section */}
                    <div className="px-4 py-3 border-b border-primary-100">
                      <p className="text-sm font-medium text-primary-text">
                        {profile ? `${profile.first_name} ${profile.last_name}` : 'Loading...'}
                      </p>
                      <p className="text-xs text-primary-footerText mt-1 capitalize">
                        {profile?.user_type || 'Loading...'}
                      </p>
                    </div>

                    {/* Menu Items */}
                    {getProfileMenuItems().map((item, index) => (
                      <Link
                        key={index}
                        to={item.path}
                        className="block px-4 py-2 text-sm text-primary-text hover:bg-primary-50"
                        onClick={() => setShowProfileMenu(false)}
                      >
                        {item.label}
                      </Link>
                    ))}

                    <button
                      onClick={() => {
                        handleSignOut();
                        setShowProfileMenu(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-primary-text hover:bg-primary-50"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/signup"
                  className="bg-primary-500 text-primary-50 px-6 py-2.5 rounded-full hover:bg-opacity-90 transition-colors duration-200 text-sm font-medium"
                >
                  Sign Up Now
                </Link>
                <Link
                  to="/login"
                  className="text-primary-text hover:text-primary-500 px-6 py-2.5 rounded-full border border-primary-200 hover:bg-primary-50 transition-colors duration-200 text-sm font-medium"
                >
                  Login
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Mobile navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4">
            {navigationLinks.map((link, index) => (
              <a
                key={index}
                href={link.href}
                className="block py-2 text-primary-text hover:text-primary-500 transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                {link.title}
              </a>
            ))}
            {user ? (
              <div className="mt-4 space-y-2">
                {/* User Info Section for Mobile */}
                <div className="py-3 border-y border-primary-100">
                  <p className="text-sm font-medium text-primary-text">
                    {profile ? `${profile.first_name} ${profile.last_name}` : 'Loading...'}
                  </p>
                  <p className="text-xs text-primary-footerText mt-1 capitalize">
                    {profile?.user_type || 'Loading...'}
                  </p>
                </div>
                {getProfileMenuItems().map((item, index) => (
                  <Link
                    key={index}
                    to={item.path}
                    className="block py-2 text-primary-text hover:text-primary-500"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
                <button
                  onClick={() => {
                    handleSignOut();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left py-2 text-primary-text hover:text-primary-500"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="mt-4 space-y-2">
                <Link
                  to="/signup"
                  className="block w-full text-center bg-primary-500 text-primary-50 px-4 py-2 rounded-full hover:bg-opacity-90 transition-colors duration-200"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign Up Now
                </Link>
                <Link
                  to="/login"
                  className="block w-full text-center text-primary-text hover:text-primary-500 px-4 py-2 rounded-full border border-primary-200 hover:bg-primary-50 transition-colors duration-200"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Login
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navigation;